import { FilterOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Drawer, Input, Space, message } from 'antd';
import { debounce } from 'lodash-es';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useFetcher, useLoaderData, useSearchParams } from 'react-router-dom';
import { emailTemplateMultilingualOff, emailTemplateMultilingualOn, getEmailTemplates } from '../../api/settings/email-template.js';
import { SelectedFilters } from '../../components/data/selected-filters.jsx';
import TemplateFilterForm from '../../components/form/template-filter/template-filter.jsx';
import { Page } from '../../components/page';
import Table from '../../components/table/table.jsx';
import { useFetch } from '../../hooks';
import { TemplateListColumns } from '../../library/constants/index.js';
import { searchParamsToObject } from '../../library/helpers/index.js';

const FILTER_NAMES = {
  active: 'Status',
  multilingual: 'Multilingual',
  labels: 'Labels',
  search: 'Search',
};

function EmailTemplateList(props) {
  const { title } = props;
  const initialData = useLoaderData();
  const [, setSearchParams] = useSearchParams();
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [list, hasMore, isLoading, searchParamsParsed, {
    setSort,
    setFilters,
    loadMore,
  }] = useFetch({ initialData });

  const multilingualFetcher = useFetcher();
  const debouncedChangeHandler = debounce((q) => setFilters({ search: q }), 500);

  return (
    <Page
      title={title}
      summary={initialData?.meta?.totalCount ? `(${initialData?.meta?.totalCount} Records)` : '(No Records)'}
      header={(
        <>
          <div></div>
          <Space.Compact style={{ width: '100%', maxWidth: '400px' }}>
            <Input.Search
              prefix={<SearchOutlined />}
              placeholder="Search"
              onChange={(e) => {
                if (e.target.value === '') {
                  setSearchParams((sp) => {
                    sp.delete('filter[search]');
                    return sp;
                  });
                  return;
                }
                return debouncedChangeHandler(e.target.value);
              }}
              defaultValue={searchParamsParsed?.filter?.search ?? ''}
              allowClear
            />
            <Button icon={<FilterOutlined />} onClick={() => setFiltersVisible((v) => !v)}>
              Filters
            </Button>
          </Space.Compact>
          <div></div>
        </>
      )}
      subHeader={initialData.meta?.filters && (
        <SelectedFilters
          filters={initialData.meta.filters}
          filterNames={FILTER_NAMES}
          updateFilters={setFilters}
        />
      )}
    >
      <Drawer
        open={filtersVisible}
        onClose={() => setFiltersVisible(false)}
        title="Filters"
      >
        <TemplateFilterForm
          key={JSON.stringify(searchParamsParsed.filter)}
          data={searchParamsParsed.filter}
          onFinish={(values) => {
            const filters = {
              ...values,
              labels: values.labels?.length ? values.labels.join(',') : undefined,
            };
            setFilters(filters);
            setFiltersVisible(false);
          }}
        />
      </Drawer>

      <Table
        columns={TemplateListColumns(multilingualFetcher)}
        scroll={{ x: '100%' }}
        data={list}
        rowKey="id"
        isLoading={isLoading}
        onChange={setSort}
        hasMore={hasMore}
        loadMore={loadMore}
      />
    </Page>
  );
}

EmailTemplateList.Loader = async ({ request }) => {
  const url = new URL(request.url);
  const { filter, page, sort } = searchParamsToObject(url.searchParams);
  const { data, meta } = await getEmailTemplates({ filter, page, ...sort });
  return { data, meta };
};

EmailTemplateList.Actions = {
  multilingual: async ({ method, data }) => {
    const { id } = data;
    try {
      const msg = method.toLowerCase() === 'delete'
        ? await emailTemplateMultilingualOff(id)
        : await emailTemplateMultilingualOn(id);
      message.success(msg);
      return true;
    } catch (error) {
      message.error(error.message);
      return { errors: error.error };
    }
  },
};

EmailTemplateList.propTypes = {
  title: PropTypes.string,
};

export default EmailTemplateList;
